import React, { useEffect, useRef, useState } from "react";
// navbar.css also holds the site-wide .contact-us-button style.
import "../../assets/styles/navbar.css";
import "./header.css";
import dobedo_logo from "../../assets/img/DOBEDO_LOGO.png";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import BookCallButton from "components/booking/BookCallButton";
import { CONTACT, SERVICES, SOCIALS } from "config/site";
import { fetchActivePostings } from "services/hiring";

// One lookup per page load, shared by every Navbar instance.
let openRolesPromise;
const getOpenRoleCount = () => {
  if (!openRolesPromise) {
    openRolesPromise = fetchActivePostings()
      .then((postings) => postings.length)
      .catch(() => 0);
  }
  return openRolesPromise;
};

const NAV_LINKS = [
  { to: "/generative-ai", label: "AI Solutions" },
  { to: "/work", label: "Our Work" },
  { to: "/join-our-team", label: "Careers", hiring: true },
  { to: "/about-us", label: "About" },
];

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [openRoles, setOpenRoles] = useState(0);
  const closeTimer = useRef();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let alive = true;
    getOpenRoleCount().then((n) => alive && setOpenRoles(n));
    return () => {
      alive = false;
    };
  }, []);

  // Close menus after navigating.
  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isServicesActive = SERVICES.some((s) => s.path === location.pathname);

  const openServices = () => {
    clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  // A short delay lets the pointer travel from the trigger into the panel.
  const closeServices = () => {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 120);
  };

  const goToAllServices = (e) => {
    e.preventDefault();
    setServicesOpen(false);
    setMenuOpen(false);
    const scroll = () =>
      document.getElementById("services")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (location.pathname === "/") scroll();
    else {
      navigate("/");
      setTimeout(scroll, 400);
    }
  };

  const hiringPill = (
    <span className="site-hiring-pill" title={`${openRoles} open position${openRoles > 1 ? "s" : ""}`}>
      Hiring
    </span>
  );

  return (
    <header className="site-header-wrap">
      <nav id="navbar-main" className={`site-header ${scrolled ? "is-scrolled" : ""}`} aria-label="Main">
        <div className="container site-header-inner">
          <Link to="/" className="site-logo" aria-label="Dobedo Soft home">
            <img src={dobedo_logo} alt="Dobedo Soft" />
          </Link>

          <ul className="site-nav">
            <li className="site-nav-item" onMouseEnter={openServices} onMouseLeave={closeServices}>
              <button
                type="button"
                className={`site-nav-link ${isServicesActive ? "active" : ""}`}
                aria-expanded={servicesOpen}
                onClick={() => setServicesOpen((v) => !v)}
              >
                Services <i className={`fa-solid fa-chevron-down site-caret ${servicesOpen ? "open" : ""}`} />
              </button>
              <div className={`site-mega ${servicesOpen ? "show" : ""}`}>
                <div className="site-mega-grid">
                  {SERVICES.map((s) => (
                    <NavLink key={s.path} to={s.path} className="site-mega-item">
                      <span className="site-mega-icon">
                        <i className={`fa-solid ${s.icon}`} />
                      </span>
                      <span>
                        <strong>{s.name}</strong>
                        <small>{s.desc}</small>
                      </span>
                    </NavLink>
                  ))}
                  <a href="/#services" className="site-mega-item site-mega-all" onClick={goToAllServices}>
                    <span className="site-mega-icon">
                      <i className="fa-solid fa-grip" />
                    </span>
                    <span>
                      <strong>All services</strong>
                      <small>See everything we do</small>
                    </span>
                  </a>
                </div>
                <div className="site-mega-promo">
                  <span className="site-mega-kicker">Not sure where to start?</span>
                  <p>Talk to us for 30 minutes and leave with a clear plan for your idea.</p>
                  <BookCallButton className="btn btn-sm btn-primary rounded site-btn-primary">
                    Book a free call
                  </BookCallButton>
                </div>
              </div>
            </li>
            {NAV_LINKS.map((link) => (
              <li className="site-nav-item" key={link.to}>
                <NavLink to={link.to} className={({ isActive }) => `site-nav-link ${isActive ? "active" : ""}`}>
                  {link.label}
                  {link.hiring && openRoles > 0 && hiringPill}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="site-actions">
            <NavLink to="/contact-us" className={({ isActive }) => `site-contact-link ${isActive ? "active" : ""}`}>
              Contact
            </NavLink>
            <BookCallButton className="btn btn-sm btn-primary rounded site-btn-primary">Book a call</BookCallButton>
            <button
              type="button"
              className={`site-burger ${menuOpen ? "open" : ""}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`site-drawer ${menuOpen ? "open" : ""}`} aria-hidden={!menuOpen}>
        <div className="site-drawer-body">
          <button
            type="button"
            className="site-drawer-link site-drawer-toggle"
            onClick={() => setMobileServicesOpen((v) => !v)}
            aria-expanded={mobileServicesOpen}
          >
            Services <i className={`fa-solid fa-chevron-down site-caret ${mobileServicesOpen ? "open" : ""}`} />
          </button>
          {mobileServicesOpen && (
            <div className="site-drawer-sub">
              {SERVICES.map((s) => (
                <NavLink key={s.path} to={s.path} className="site-drawer-sublink">
                  <i className={`fa-solid ${s.icon}`} />
                  {s.name}
                </NavLink>
              ))}
            </div>
          )}
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} className="site-drawer-link">
              {link.label}
              {link.hiring && openRoles > 0 && hiringPill}
            </NavLink>
          ))}
          <NavLink to="/contact-us" className="site-drawer-link">
            Contact
          </NavLink>

          <div className="site-drawer-cta">
            <BookCallButton className="btn btn-primary rounded w-100 site-btn-primary">
              Book a discovery call
            </BookCallButton>
          </div>

          <div className="site-drawer-foot">
            <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            <a href={CONTACT.phoneHref}>{CONTACT.phone}</a>
            <div className="site-socials">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                  <i className={s.icon} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
